/**
 * 공통코드 — cm_code_group / cm_code
 * 그룹은 플랫, 상세는 동일 그룹 내 parentCode 계층.
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList } from 'services/commonQuery';

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

export const MODULE_OPTIONS = [
  { value: 'COMMON', label: '공통' },
  { value: 'CM', label: 'CM' },
  { value: 'HR', label: 'HR' },
  { value: 'BD', label: '예산' },
];

export const SAVE_QUERY_GROUP = [
  'CmCodeGroup.insertCodeGroup',
  'CmCodeGroup.updateCodeGroup',
  'CmCodeGroup.deleteCodeGroup',
];

export const SAVE_QUERY_CODE = [
  'CmCode.insertCode',
  'CmCode.updateCode',
  'CmCode.deleteCode',
];

export function moduleLabel(code) {
  return MODULE_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

/** 상위코드·depth 보정 (동일 그룹 목록 기준) */
export function resolveCodeDepth(row, allRows = []) {
  const parent = String(row?.parentCode || '').trim();
  if (!parent) return 1;
  const parentRow = (allRows || []).find(
    (r) => r && r.mod !== 'D' && String(r.code) === parent
  );
  if (!parentRow) return 1;
  const pd = Number(parentRow.depth);
  return Number.isFinite(pd) && pd > 0 ? pd + 1 : 2;
}

export function enrichCodeRows(rows) {
  return (rows || []).map((r) => ({
    ...r,
    parentCode: r.parentCode || '',
    depth: Number(r.depth) || 1,
  }));
}

export async function loadCodeGroups(params = {}) {
  if (!isBdApiEnabled()) return [];
  return commonSelect('CmCodeGroup.selectCodeGroupList', params);
}

export async function loadCodes(params = {}) {
  if (!isBdApiEnabled()) return [];
  const list = await commonSelect('CmCode.selectCodeList', params);
  return enrichCodeRows(list);
}

/** 콤보용 — useYn=Y 만, sort_order 순 */
export function codesToOptions(rows = []) {
  return (rows || [])
    .filter((r) => r && r.mod !== 'D' && r.useYn !== 'N' && r.code)
    .map((r) => ({
      value: String(r.code),
      label: String(r.codeNm || r.code),
      depth: Number(r.depth) || 1,
      parentCode: r.parentCode || '',
      attr1: r.attr1 || '',
    }));
}

/**
 * 공통코드 그룹 → 콤보 옵션
 * @param {string} codeGroup
 * @returns {Promise<Array<{value:string,label:string}>>}
 */
export async function loadCodeOptions(codeGroup) {
  const g = String(codeGroup || '').trim();
  if (!g) return [];
  const rows = await loadCodes({ codeGroup: g, useYn: 'Y' });
  return codesToOptions(rows);
}

export async function saveCodeGroups(rows) {
  if (!isBdApiEnabled()) {
    throw new Error('공통코드는 API 모드에서만 저장할 수 있습니다.');
  }
  return commonSaveList(SAVE_QUERY_GROUP, rows);
}

export async function saveCodes(rows) {
  if (!isBdApiEnabled()) {
    throw new Error('공통코드는 API 모드에서만 저장할 수 있습니다.');
  }
  const prepared = (rows || []).map((r) => {
    if (!r || (r.mod !== 'A' && r.mod !== 'U' && r.mod !== 'D')) return r;
    const parentCode = String(r.parentCode || '').trim();
    return {
      ...r,
      parentCode,
      depth: resolveCodeDepth({ ...r, parentCode }, rows),
    };
  });
  return commonSaveList(SAVE_QUERY_CODE, prepared);
}

export function filterCodeGroups(rows, { codeGroup, codeGroupNm, moduleCd, useYn } = {}) {
  return (rows || []).filter((r) => {
    if (!r || r.mod === 'D') return false;
    if (codeGroup && !String(r.codeGroup || '').includes(String(codeGroup))) return false;
    if (codeGroupNm && !String(r.codeGroupNm || '').includes(String(codeGroupNm))) return false;
    if (moduleCd && r.moduleCd !== moduleCd) return false;
    if (useYn && r.useYn !== useYn) return false;
    return true;
  });
}

export function validateCodeGroups(rows) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const seen = new Set();
  for (const r of active) {
    const g = String(r.codeGroup || '').trim();
    if (!g) return { ok: false, message: '코드그룹 ID를 입력하세요.' };
    if (!String(r.codeGroupNm || '').trim()) {
      return { ok: false, message: `그룹 ${g}: 그룹명을 입력하세요.` };
    }
    if (seen.has(g)) return { ok: false, message: `코드그룹 ID 중복: ${g}` };
    seen.add(g);
  }
  for (const r of rows || []) {
    if (r?.mod === 'D' && r.systemYn === 'Y') {
      return { ok: false, message: `시스템 고정 그룹은 삭제할 수 없습니다: ${r.codeGroup}` };
    }
  }
  return { ok: true };
}

/**
 * @param {Array} rows - 해당 그룹 상세
 * @param {{ deletingGroup?: boolean }} [opts]
 */
export function validateCodes(rows, opts = {}) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const byCode = new Map(active.map((r) => [String(r.code), r]));
  const seen = new Set();

  for (const r of active) {
    const code = String(r.code || '').trim();
    if (!code) return { ok: false, message: '코드를 입력하세요.' };
    if (!String(r.codeNm || '').trim()) {
      return { ok: false, message: `코드 ${code}: 코드명을 입력하세요.` };
    }
    if (seen.has(code)) return { ok: false, message: `코드 중복: ${code}` };
    seen.add(code);

    const parent = String(r.parentCode || '').trim();
    if (parent) {
      if (parent === code) {
        return { ok: false, message: `코드 ${code}: 상위코드는 자신일 수 없습니다.` };
      }
      if (!byCode.has(parent)) {
        return { ok: false, message: `코드 ${code}: 상위코드 ${parent} 가 없습니다.` };
      }
    }
  }

  // 삭제 대상이 하위의 상위이면 거부
  for (const r of rows || []) {
    if (r?.mod !== 'D') continue;
    const code = String(r.code || '').trim();
    const child = active.find((c) => String(c.parentCode || '').trim() === code);
    if (child) {
      return {
        ok: false,
        message: `코드 ${code}: 하위 코드(${child.code})가 있어 삭제할 수 없습니다.`,
      };
    }
  }

  if (opts.deletingGroup && active.length) {
    return { ok: false, message: '상세 코드가 있는 그룹은 삭제할 수 없습니다. 상세를 먼저 삭제하세요.' };
  }

  return { ok: true };
}
